/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState,
  ListActionPipelines,
  UsageDialogConfig,
  usageDialogRoute,
} from '@gh/builder-shared';
import { BuilderPermissionSet, CSMasterListItemStatus, UsageEntityType } from '@gh/builder-shared-data';
import { AppRouter, UserContext } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { controlTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import { DialogService, newFormControl, NotificationService } from '@gh/core-ui';
import { cursorTracker, GridRowEvent, newGridPaginationCursor } from '@gh/core-ui-grid';
import { filter$ } from '@gh/rx/operators';
import { CSMasterBusinessService } from '../../resources/cs-master-business.service';
import { CloneSource, CSMasterListItem } from '../../resources/cs-master.model';
import {
  CSMasterCloneDialogComponent,
  CSMasterCloneDialogConfig,
} from '../cs-master-clone-dialog/cs-master-clone-dialog.component';
import { CSMasterGridDefinition } from './cs-master-list-grid-definition.service';

@Component({
  selector: 'gh-cs-master-list-tab',
  template: require('./cs-master-list-tab.component.html'),
  providers: [CSMasterGridDefinition],
})
export class CSMasterListTabComponent implements OnInit {
  dataSource: SlicedDataSource<CSMasterListItem>;
  cursor = newGridPaginationCursor();
  showDeleted = newFormControl(false);

  constructor(private businessService: CSMasterBusinessService,
              public gridDefinition: CSMasterGridDefinition,
              private router: AppRouter,
              private route: ActivatedRoute,
              private routeData: RouteDataService,
              private navigationService: BuilderNavigationService,
              private pipelines: ListActionPipelines,
              private dialogService: DialogService,
              private commonActions: CommonActions,
              private notificationService: NotificationService,
              private userContext: UserContext) {

  }

  isActive(item: CSMasterListItem): boolean {
    return item.statusValue === CSMasterListItemStatus.Active;
  }

  ngOnInit(): void {
    const tracker = this.routeData.tracker(controlTracker(this.showDeleted)).withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(() => this.loadData());
  }

  onClone(): void {
    this.dialogService.openWithInput(CSMasterCloneDialogComponent, <CSMasterCloneDialogConfig>{}).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((cloneData: any) => {
        const queryParams = cloneData.source === CloneSource.CSMaster ? { cloneFromCsMaster: cloneData.value.id } :
          { cloneFromJobCs: cloneData.value.id };

        this.router.navigateToChild(
          ['/setup/job-admin/cs-masters/new'],
          { queryParams });
      },         this.notificationService.processAll);
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/job-admin/cs-masters/new']);
  }

  onOpen(item: CSMasterListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Setup.JobAdmin.ColorSelectionMasters.EDIT_VIEW)) {
      this.router.navigateToChild(['setup/job-admin/cs-masters/', item.id]);
    }
  }

  onViewUsage(item: CSMasterListItem): void {
    this.dialogService.openWithInput(usageDialogRoute, <UsageDialogConfig>{
      entityId: item.id,
      entityType: UsageEntityType.CS_MASTER_USAGE_INFO,
    });
  }

  onActivate(item: CSMasterListItem): void {
    const flow = this.pipelines.toggleItem({
      question: {
        id: 'msg_cs_master_undelete',
        params: {
          name: item.name,
        },
      },
      execute: this.commonActions.execute((state: CommonFlowState<CSMasterListItem>) =>
        this.businessService.activate(state.value)),
      finish: () => this.loadData(),
    });

    flow.run({
      value: item,
    });
  }

  onDeactivate(item: CSMasterListItem): void {
    const flow = this.pipelines.checkUsageToggleItem({
      messages: {
        confirmUsed: {
          id: 'msg_cs_master_mark_as_deleted',
          params: {
            name: item.name,
          },
        },
        confirmNotUsed: {
          id: 'msg_cs_master_delete',
          params: {
            name: item.name,
          },
        },
      },
      executes: {
        checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.checkUsage(state.value)),
        summary: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.deactivateOrDelete(item, state.value)),
      },
      finish: () => this.loadData(),
    });

    flow.run({
      value: item,
    });
  }

  onRowClicked(event: GridRowEvent<CSMasterListItem>): void {
    this.onOpen(event.data);
  }

  goToAel(id?: ID): void {
    this.navigationService.goToAel({
      entityType: 'CS_MASTER',
      entityId: id,
      label: 'lbl_color_selection',
    });
  }

  private loadData(): void {
    this.dataSource = this.businessService.findAllAsDataSource(this.showDeleted.value);
  }
}
